import {
  Amenity,
  Bedroom,
  Cashback,
  MealPlan,
  Occupancy,
  RoomImage,
  RoomsSplitBooking
} from '@findhotel/sapi'

import {Guests} from '../../api-types/bovio/response/booking'
import {Offer, OfferPrice} from '../../offer/types/offer'

/**
 * Represents the types of bed available for the user (Single, Double, Cot)
 */
export interface BedType {
  id: string
  name: string
}

export interface RoomArea {
  squareMeters?: number
  squareFeet?: number
}

/**
 * A {@link Room} type represents a real room in an accomodation that is possible to be booked.
 * It contains among other things a list of {@link Offer}
 */
export interface Room {
  /** Ammenities present in the room. */
  amenities: string[]
  /** List of amenities with IDs and titles. */
  amenitiesList?: Amenity[]
  bedTypes: BedType[]
  bedrooms?: Bedroom[]
  /** The room long description. */
  description: string
  id: string
  /** Room master ID is created when multiple rooms are merged/squashed into one. */
  masterId?: string
  images: RoomImage[]
  /** The name of the room listed in the room list. */
  name: string
  /** How many people fit in the room */
  occupationPerRoom: number
  occupancy?: Occupancy
  /** The list of possible offers for that room. */
  offers: Offer[]
  /** Whether that room can be chosen for smokers. */
  smokingOptionsAvailable?: boolean
  /** Number of guests */
  guests?: Guests
  /** Guest's first name */
  firstName?: string
  /** Guest's last name */
  lastName?: string
  /** Ids of other rooms sqhashed into current one */
  squashedIds: string[]
  /** Whether the room has an offer that was selected by the user on SRP. Added for sapi4eva-match-clicked-offer experiment */
  hasClickedOffer?: boolean
  /** The name of the RAA */
  raaName?: string
  /** The room area square in meters and feet */
  area?: RoomArea
  /** SAPI meal plan entries */
  mealPlan?: MealPlan[]
}

/**
 * Unified room type for checkout where data may be coming from different sources
 * Those sources are: OfferCheck or ExistingBooking
 *
 * NOTE:
 * - We should avoid creating other types
 * - We should aim to unify this on the backend response level
 */
export type RoomWithoutOffers = Omit<Room, 'offers' | 'squashedIds'> & {
  checkIn?: string
  checkOut?: string
}

export interface SplitBookingOffer
  extends Omit<RoomsSplitBooking['offers'][number], 'offer'> {
  offer: Offer
}

export interface SplitBooking
  extends Omit<RoomsSplitBooking, 'offers' | 'prices'> {
  offers: Array<SplitBookingOffer>
  prices: OfferPrice[]
  cashback?: Cashback
}

export interface AnalyticsSplitBooking extends SplitBooking {
  isHidden: boolean
}

export interface RoomSplitBookingContext extends SplitBooking {
  isHidden?: boolean
}

export type SplitBookingDates = {
  checkIn: string
  splitDate: string
  checkOut: string
}

export enum RoomCardEntityType {
  Regular = 'regular',
  SplitBooking = 'splitBooking'
}

export type RoomCardEntity =
  | {
      id: string
      type: RoomCardEntityType.Regular
      data: Room
    }
  | {
      id: string
      type: RoomCardEntityType.SplitBooking
      data: SplitBooking
    }
